import { type ReactNode } from "react";
import { type components } from "@njbsoft/types";

export type BadgeVariant = "default" | "success" | "warning" | "danger" | "info" | "outline";

interface BadgeProps {
  variant?: BadgeVariant;
  children: ReactNode;
  className?: string;
}

const variantClasses: Record<BadgeVariant, string> = {
  success: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300",
  warning: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  danger: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  info: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  default: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
  outline: "border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300",
};

export function Badge({ variant = "default", children, className = "" }: BadgeProps) {
  // Below sm a badge in a narrow cell wraps instead of forcing the cell wide.
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium max-sm:whitespace-normal ${variantClasses[variant]} ${className}`}
    >
      {children}
    </span>
  );
}

// The live FacilityComplianceStatus enum is no_active_devices |
// out_of_compliance | pending_review | compliant. NOTE: pending_review maps to
// `warning` (amber) HERE, but to `info` (blue) in entityStatusVariant below —
// same string, two different axes, two intentionally different colors. Do NOT
// homogenize the two helpers; the compliance axis and the entity-lifecycle axis
// carry different operator meaning for pending_review.
export function complianceStatusVariant(status: string | null | undefined): BadgeVariant {
  switch (status) {
    case "out_of_compliance":
      return "danger";
    case "pending_review":
      return "warning";
    case "compliant":
      return "success";
    case "no_active_devices":
      return "default";
    default:
      return "default";
  }
}

export function entityStatusVariant(status: string | null | undefined): BadgeVariant {
  switch (status) {
    case "active":
      return "success";
    case "inactive":
      return "warning";
    case "pending_review":
      return "info";
    case "archived":
      return "default";
    default:
      return "default";
  }
}

type CatalogStatus = "approved" | "previously_approved" | "suspended";

/**
 * Variant mapping for the live `CatalogStatus` enum (USC FCCCHR
 * 3-value vocabulary: approved / previously_approved / suspended).
 * Exhaustive switch with a `default` clause that throws via the
 * `never`-exhaustiveness pattern — adding a new enum value to the
 * spec without updating this function will fail typecheck.
 */
export function catalogStatusVariant(status: CatalogStatus): BadgeVariant {
  switch (status) {
    case "approved":
      return "success";
    case "previously_approved":
      return "warning";
    case "suspended":
      return "danger";
    default: {
      const _exhaustive: never = status;
      throw new Error(`Unhandled CatalogStatus value: ${String(_exhaustive)}`);
    }
  }
}

type NonComplianceReason = components["schemas"]["NonComplianceReason"];

/**
 * Variant mapping for the `NonComplianceReason` enum. test_overdue /
 * test_failed / lifecycle_terminal are danger (a regulatory test gap or a
 * historical-artifact device); out_of_service, no_test_on_record and
 * test_pending_review are warning (operator-declared out-of-service, a device
 * simply missing any passing test on record, or a submitted test still queued
 * for review — amber, not a confirmed failure). Exhaustive switch
 * with `never`-exhaustiveness guard — adding a new enum value to the spec
 * without updating this function fails typecheck.
 */
export function nonComplianceReasonVariant(reason: NonComplianceReason): BadgeVariant {
  switch (reason) {
    case "test_overdue":
      return "danger";
    case "test_failed":
      return "danger";
    case "lifecycle_terminal":
      return "danger";
    case "out_of_service":
      return "warning";
    case "no_test_on_record":
      return "warning";
    case "test_pending_review":
      return "warning";
    default: {
      const _exhaustive: never = reason;
      throw new Error(`Unhandled NonComplianceReason value: ${String(_exhaustive)}`);
    }
  }
}

type FacilityLetterCodeStatus = components["schemas"]["FacilityLetterCodeStatus"];

/**
 * Variant mapping for the live `FacilityLetterCodeStatus` enum. `live` is the
 * only state a code can still be redeemed in (success); `expired` is the
 * ordinary end of a code's life (neutral); `revoked` is an operator retiring a
 * credential in someone's hands (danger). Exhaustive switch with a
 * `never`-exhaustiveness guard.
 */
export function letterCodeStatusVariant(status: FacilityLetterCodeStatus): BadgeVariant {
  switch (status) {
    case "live":
      return "success";
    case "expired":
      return "default";
    case "revoked":
      return "danger";
    default: {
      const _exhaustive: never = status;
      throw new Error(`Unhandled FacilityLetterCodeStatus value: ${String(_exhaustive)}`);
    }
  }
}

type ComplianceVerdict = components["schemas"]["ComplianceVerdict"];

/**
 * Variant mapping for the live `ComplianceVerdict` enum. terminal / failed /
 * overdue are danger (an active non-compliance the dashboard surfaces);
 * out_of_service, unverified and pending_review are warning (unverified = no
 * passing test on record; pending_review = a submitted test still queued for
 * review — honest amber, not a confirmed failure); compliant is success.
 * Exhaustive switch with `never`-exhaustiveness guard.
 */
export function complianceVerdictVariant(verdict: ComplianceVerdict): BadgeVariant {
  switch (verdict) {
    case "terminal":
      return "danger";
    case "failed":
      return "danger";
    case "overdue":
      return "danger";
    case "out_of_service":
      return "warning";
    case "unverified":
      return "warning";
    case "pending_review":
      return "warning";
    case "compliant":
      return "success";
    default: {
      const _exhaustive: never = verdict;
      throw new Error(`Unhandled ComplianceVerdict value: ${String(_exhaustive)}`);
    }
  }
}
